<template>
  <div
    class="review-tags-settings-list"
    :class="{ editable: canEdit }"
  >
    <review-tags-settings-list-item
      v-for="tag in itemList"
      :key="`tag-item:${tag.id}`"
      :tag="tag"
      :can-edit="canEdit && !editingTag"
      :editing="editingTag === tag"
      :error-msg="editingTag === tag ? editingErrorMsg : null"
      :disabled="disabled"
      :new-row="isNewTag(tag)"
      :body-width="bodyWidth"
      :small="small"
      class="mb-3"
      @createTag="createTag()"
      @updateTag="(newTag) => updateTag(newTag)"
      @deleteTag="deleteTag(tag)"
      @requestEdit="requestEdit(tag)"
      @cancelEdit="cancelEdit()"
    />
    <div
      v-if="itemList.length === 0"
      :style="{ 'min-width': bodyWidth }"
    ></div>
    <v-btn
      v-if="canEdit"
      :disabled="disabled || !!editingTag"
      color="success"
      class="text-none mt-2"
      :width="small ? '100%' : bodyWidth"
      @click="createTag()"
    >
      <v-icon left>add</v-icon>
      <span class="font-weight-bold">Add</span>
    </v-btn>
  </div>
</template>

<script>
import { Tag } from '@/models';
import { typeHelpers } from '@/modules';
import ReviewTagsSettingsListItem from './ReviewTagsSettingsListItem';

const NewTagId = 'NEW';

export default {
  name: 'ReviewTagsSettingsList',
  components: {
    ReviewTagsSettingsListItem,
  },
  props: {
    tags: {
      type: Array,
      required: true,
      validator: (tags) => typeHelpers.isArrayOfType(tags, Tag),
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    bodyWidth: {
      type: String,
      default: '20rem',
    },
    small: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['createTag', 'updateTag', 'deleteTag'],
  data: () => ({
    editingTag: null,
    editingErrorMsg: null,
  }),
  computed: {
    itemList() {
      if (this?.editingTag?.id === NewTagId) {
        return [...this.tags, this.editingTag];
      }
      return this.tags;
    },
  },
  methods: {
    deleteTag(tag) {
      this.$emit('deleteTag', tag);
    },
    updateTag(tag) {
      const errorMsg = this.computeTagError(tag);
      this.editingErrorMsg = errorMsg;
      if (errorMsg) {
        return;
      }

      if (tag.id === NewTagId) {
        this.$emit('createTag', tag);
      } else {
        this.$emit('updateTag', tag);
      }
      this.editingTag = null;
    },
    createTag() {
      this.editingErrorMsg = null;
      this.editingTag = new Tag({ id: NewTagId });
    },
    requestEdit(tag) {
      this.editingErrorMsg = null;
      this.editingTag = tag;
    },
    cancelEdit() {
      this.editingErrorMsg = null;
      this.editingTag = null;
    },
    isNewTag(tag) {
      return tag.id === NewTagId;
    },
    computeTagError(newTag) {
      const { label, id } = newTag;

      if (label.length === 0) {
        return 'Tag cannot be empty';
      } else if (label.length > 25) {
        return 'Tag exceeds limit of 25 characters';
      }

      for (const existingTag of this.tags) {
        if (label === existingTag.label && id !== existingTag.id) {
          return `'${label}' duplicates an existing tag'`;
        }
      }

      return null;
    },
  },
};
</script>

<style scoped lang="scss">
.review-tags-settings-list {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 0.5rem;
  justify-items: start;
}
</style>
